import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { useCustomerAuth } from "@/lib/customerAuth";

const HIGHLIGHTS = [
  "See every submission and payout in one place",
  "Bring in past guest submissions from the same email",
  "Faster checkout — payout details pre-filled",
  "Referral link + bonus tracker included",
];

export default function SignInPage() {
  const { signIn } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = new URLSearchParams(location.search).get("next") || "/dashboard";
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [slow, setSlow] = useState(false);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const onPwKey = (e) => {
    if (typeof e.getModifierState === "function") setCapsOn(e.getModifierState("CapsLock"));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSlow(false);
    const slowTimer = setTimeout(() => setSlow(true), 8000);
    try {
      await signIn(form);
      toast.success("Welcome back!");
      navigate(redirectTo);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (!err?.response) {
        try {
          await signIn(form);
          toast.success("Welcome back!");
          navigate(redirectTo);
          return;
        } catch (err2) {
          toast.error(err2?.response?.data?.detail || "Couldn't reach our servers. Please try again.");
        }
      } else if (status === 401) toast.error("Wrong email or password. Try Forgot password?");
      else toast.error(detail || "Couldn't sign you in.");
    } finally {
      clearTimeout(slowTimer);
      setSlow(false);
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Sign in" />
      <section className="sign-up-section" data-testid="signin-hero">
        <div className="sign-up-container">
          <div className="sign-up-text-container">
            <div style={{ display: "inline-block", padding: "6px 14px", borderRadius: 90, background: "#eaf0ff", color: "#346af8", fontSize: 12, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 22 }}>
              Sign in
            </div>
            <div className="sign-up-heading">
              <h1 className="h1 _700-bold">Welcome back to <span style={{ color: "#346af8" }}>your dashboard</span></h1>
            </div>
            <div className="sign-up-para">
              <p className="main-body-text _500-medium">Sign in to see every submission, payout, and reference in one place.</p>
            </div>
            <div className="sign-up-list" style={{ marginTop: 30 }}>
              {HIGHLIGHTS.map((c) => (
                <div className="why-us-list-row" key={c} style={{ marginBottom: 12 }}>
                  <figure className="check-icon-wrapper">
                    <img src="/jumbo/images/checkamrk.svg" loading="lazy" alt="" className="check-icon" />
                  </figure>
                  <div>{c}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="sign-up-form-div-wrapper">
            <div className="sign-up-form-wrapper" style={{ maxWidth: 440 }}>
              <form
                onSubmit={onSubmit}
                data-testid="customer-signin-form"
                style={{ background: "#fff", padding: 34, borderRadius: 24, border: "1px solid #eaeaf0", boxShadow: "0 30px 60px -30px rgba(41,45,85,.3)" }}
              >
                <h3 className="h4 _700-bold" style={{ marginBottom: 6 }}>Sign in</h3>
                <p style={{ color: "#6c6376", fontSize: 14, marginBottom: 24 }}>New here? <Link to="/signup" data-testid="link-signup" style={{ color: "#346af8", fontWeight: 700 }}>Create an account →</Link></p>

                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Email</label>
                  <input
                    data-testid="signin-email"
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    style={inputStyle}
                  />
                </div>

                <div style={{ marginBottom: 6 }}>
                  <label style={labelStyle}>Password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      data-testid="signin-password"
                      type={show ? "text" : "password"}
                      value={form.password}
                      onChange={onChange("password")}
                      onKeyDown={onPwKey}
                      onKeyUp={onPwKey}
                      required
                      autoComplete="current-password"
                      placeholder="••••••••"
                      style={{ ...inputStyle, paddingRight: 60 }}
                    />
                    <button type="button" onClick={() => setShow((v) => !v)} style={showBtn}>{show ? "Hide" : "Show"}</button>
                  </div>
                  {capsOn && <div data-testid="signin-caps-warning" style={{ fontSize: 12, color: "#b45309", marginTop: 6, fontWeight: 700 }}>⚠️ Caps Lock is ON</div>}
                </div>

                <div style={{ textAlign: "right", marginBottom: 22 }}>
                  <Link to="/reset-password" data-testid="link-forgot-password" style={{ fontSize: 12.5, color: "#346af8", fontWeight: 600, textDecoration: "none" }}>Forgot password?</Link>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="signin-submit"
                  className="primary-button w-button"
                  style={{ width: "100%", border: "none", cursor: loading ? "wait" : "pointer", opacity: loading ? 0.7 : 1 }}
                >
                  {loading ? (slow ? "Waking up our servers…" : "Signing in…") : "Sign in"}
                </button>
                {slow && <p style={{ fontSize: 12, color: "#b45309", textAlign: "center", marginTop: 10 }}>First sign-in after a quiet period can take ~10 seconds.</p>}
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

const labelStyle = { display: "block", fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#0b0d17", marginBottom: 8 };
const inputStyle = { width: "100%", padding: "13px 16px", borderRadius: 12, border: "1px solid #d8dcec", background: "#fff", color: "#0b0d17", fontSize: 15, outline: "none" };
const showBtn = { position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", fontSize: 12, fontWeight: 600, color: "#6c6376", cursor: "pointer" };
